// Approximate location for an enquiry, resolved from the visitor's IP by a
// third-party lookup. We keep only the city/region/country it returns — never
// the IP itself — and the request is fired lazily (see Contact.tsx) so visitors
// who never fill in the form are never looked up at all.

type GeoProvider = {
  url: string
  // Returns the place parts, coarsest last. Null when the provider answered
  // with an error payload (both of these reply 200 with an error body).
  parse: (data: any) => Array<string | undefined> | null
}

// ipwho.is first: ipapi.co's keyless tier rate-limits shared IPs aggressively
// and answers 200 with an error body, which we treat as a miss and fall through.
const PROVIDERS: GeoProvider[] = [
  {
    url: 'https://ipwho.is/',
    parse: d => (d?.success === false ? null : [d?.city, d?.region, d?.country]),
  },
  {
    url: 'https://ipapi.co/json/',
    parse: d => (d?.error ? null : [d?.city, d?.region, d?.country_name]),
  },
]

const TIMEOUT_MS = 4000

const lookup = async (provider: GeoProvider): Promise<string> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const res = await fetch(provider.url, { signal: controller.signal })
    if (!res.ok) return ''
    const parts = provider.parse(await res.json())
    if (!parts) return ''
    return parts.filter(Boolean).join(', ')
  } catch {
    return ''
  } finally {
    clearTimeout(timer)
  }
}

// Never rejects — location is a nice-to-have, so a provider being down or
// rate-limited must never stand between a visitor and sending their enquiry.
export const fetchLocation = async (): Promise<string> => {
  for (const provider of PROVIDERS) {
    const location = await lookup(provider)
    if (location) return location
  }
  return ''
}
